'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import { screenGenerationPrompt } from '@/shared/infrastructure/content-safety/contentSafety';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { StoryOrchestrator, type CreateStoryInput } from '@/contexts/stories/orchestration/StoryOrchestrator';
import type { PipelineConfig } from '@/contexts/stories/types/pipeline';

export interface CreateStoryActionInput {
  prompt?: string;
  text?: string;
  title?: string;
  config: PipelineConfig;
}

export interface CreateStoryActionResult {
  storyId: string;
  jobId: string;
}

export async function createStory(
  input: CreateStoryActionInput,
): Promise<ActionResult<CreateStoryActionResult>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // Pre-generation IP/trademark content-safety screen (good-faith guardrail —
    // reduces, not eliminates, IP-generation risk). A 'block' verdict refuses.
    const screen = await screenGenerationPrompt({
      text: [input.prompt, input.text, input.title].filter(Boolean).join('\n'),
      context: 'story',
      identityId: userId,
    });
    if (!screen.allowed) return { success: false, error: screen.userMessage };

    const orchestrator = new StoryOrchestrator();
    const result = await orchestrator.createStory({
      familyId,
      createdBy: userId,
      prompt: input.prompt,
      text: input.text,
      title: input.title,
      config: input.config,
    });

    return {
      success: true,
      data: { storyId: result.story.id, jobId: result.job.id },
    };
  } catch (e: unknown) {
    // Free text-gen fair-use throttle → soft-push to subscribe, NOT a hard error.
    // The orchestrator throws TextGenRateLimitError (code 'TEXT_RATE_LIMIT'); we
    // surface it as a discriminated failure the client renders as a friendly
    // "upgrade for more" nudge instead of a raw error toast.
    if (isTextRateLimit(e)) {
      return {
        success: false,
        error: e.message,
        code: 'TEXT_RATE_LIMIT',
        retryAfterSec: e.retryAfterSec,
      };
    }
    return { success: false, error: actionErrorMessage('createStory', e) };
  }
}

/** Duck-typed guard for the free text-gen rate-limit (decoupled from the server-only class). */
function isTextRateLimit(
  e: unknown,
): e is { code: 'TEXT_RATE_LIMIT'; retryAfterSec: number; message: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    (e as { code?: unknown }).code === 'TEXT_RATE_LIMIT'
  );
}

export async function startStoryJob(jobId: string): Promise<ActionResult<void>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    // The sidebar single-story path runs entirely on the engine — one
    // standalone story-generation run. Generated stories use the outline→
    // story→review bundle; from-text stories use text.to_blocks (verbatim) or
    // an llm.generate transform. Either way the executor's projection writes
    // the artifacts. No in-Next AI, no legacy fallback.
    await startStoryViaEngine(jobId, familyId);
    return { success: true, data: undefined };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('createStory', e) };
  }
}

/** Engine path for a standalone sidebar story — generated or from-text. */
async function startStoryViaEngine(jobId: string, familyId: string): Promise<void> {
  const { getPipelineJobRepository, getStoryRepository } = await import(
    '@/contexts/stories/repositories'
  );
  const job = await getPipelineJobRepository().getById(jobId);
  if (!job || job.familyId !== familyId) throw new Error('Job not found');
  const story = await getStoryRepository().getById(job.storyId);
  if (!story) throw new Error('Story not found');

  const {
    buildStoryGenerationDefinition,
    buildStoryFromTextDefinition,
    buildStyleDirectives,
    storyRunStartKey,
  } = await import('@/contexts/content/services/storyPackageWorkflow');
  const { enqueueWorkflowStart } = await import('@/shared/infrastructure/queue/workflowStart');

  const config = (job.config ?? {}) as unknown as Record<string, unknown>;
  const reservationId = (job as { tokenReservation?: { reservationId?: string } })
    .tokenReservation?.reservationId ?? null;
  const language = (config.language as string) ?? story.language ?? 'ru';
  const ageGroup = (config.ageGroup as string) ?? story.ageGroup ?? '4-6';

  const sourceText = typeof config.text === 'string' ? config.text.trim() : '';

  let definition: ReturnType<typeof buildStoryGenerationDefinition>
    | ReturnType<typeof buildStoryFromTextDefinition>;
  let inputs: Record<string, unknown>;

  if (sourceText.length > 0) {
    // From-text: mode drives the story-gen step (verbatim slice / transform).
    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    // The configured LLM's model, whatever provider it is on.
    const model = await UnifiedAIConfigService.getModelForCategory('llm');
    definition = buildStoryFromTextDefinition({
      mode: (config.fromTextMode as 'verbatim' | 'translate' | 'recast_hero' | 'based_on' | 'incorporate') ?? 'verbatim',
      title: story.title ?? '',
      text: sourceText,
      language,
      ageGroup,
      heroName: (config.heroName as string | undefined) || undefined,
      model,
    });
    // The from-text step bakes title/text/language/etc. into the definition;
    // the projection only needs storyId/familyId/reservationId from inputs.
    inputs = { storyId: story.id, familyId, reservationId, language, ageGroup };
  } else {
    // Generated story: outline → story → review bundle, templated from inputs.
    const plannedBlockCount =
      (config.blockCount as number)
      ?? (await import('@/contexts/stories/utils/storyShape')).estimateStoryBlockCount(
        Number(config.desiredDurationMinutes ?? 5),
        (config.ageGroup as string) ?? story.ageGroup ?? undefined,
      );
    definition = buildStoryGenerationDefinition(plannedBlockCount);
    inputs = {
      storyId: story.id,
      familyId,
      reservationId,
      prompt: story.prompt ?? '',
      ageGroup,
      storyType: (config.storyType as string) ?? story.storyType ?? 'bedtime',
      // The genre's own beats, from its story template: without them the sidebar
      // path wrote every genre to the same shapeless three acts.
      styleDirectives: buildStyleDirectives({
        arc: (await (await import('@/contexts/templates/services/TemplateService')).TemplateService.story(
          (config.storyType as string) ?? story.storyType ?? 'bedtime',
        )).data.arc,
      }),
      language,
      blockCount: plannedBlockCount,
      mustHaveCharacters: await (await import(
        '@/contexts/content/services/ContentPlanExecutor'
      )).buildMustHaveCharactersInput(
        story.characterIds ?? [],
        familyId,
        (story.characterAssignments ?? []) as Array<{ characterId: string; role: string }>,
        // The story's language decides which spelling of each hero's name the
        // writer is given (characters.name_by_locale).
        language,
      ),
    };
  }

  await enqueueWorkflowStart({
    definition,
    startKey: storyRunStartKey(story.id),
    inputs,
  });

  // The executor's family subscription carries the projection.
  const { getContentPlanExecutor } = await import(
    '@/contexts/content/services/ContentPlanExecutor'
  );
  getContentPlanExecutor().ensureSubscription(familyId);
}
